/**
 * modes/popup.ts — popup/modal embed mode.
 *
 * Creates a full-screen backdrop + centred modal containing the iframe.
 * The modal is created once per config and cached — subsequent open() calls
 * just show/hide it rather than recreating the iframe (preserves booking state
 * if the user accidentally closes and reopens).
 *
 * WCAG 2.1 AA:
 *   - role="dialog" + aria-modal="true" + aria-label
 *   - Focus trapped inside modal while open
 *   - Escape key closes
 *   - Focus returned to trigger on close
 *   - Body scroll locked while open
 */

import { EmbedConfig } from '../config'
import { createIframe } from '../iframe-factory'
import { el } from '../dom'
import { injectStyles } from '../styles'
import { createFocusTrap } from '../a11y'
import { listenForMessages } from '../postmessage'
import { emitEvent, dispatchDOMEvent } from '../api'

interface PopupInstance {
  open:    () => void
  close:   () => void
  destroy: () => void
}

// One popup instance per org/config (shared across multiple trigger elements
// with the same config, e.g. multiple "Book now" buttons on the same page).
const cache = new Map<string, PopupInstance>()

function cacheKey(config: EmbedConfig): string {
  return JSON.stringify(config)
}

export function getOrCreatePopup(config: EmbedConfig, triggerEl?: HTMLElement): PopupInstance {
  const key = cacheKey(config)
  if (cache.has(key)) return cache.get(key)!

  injectStyles()

  // Build DOM
  const iframe  = createIframe(config)
  const closeBtn = el('button', {
    class:        'mp-close-btn',
    type:         'button',
    'aria-label': 'Close booking',
  }, '×')

  const modal = el('div', {
    class:        'mp-modal',
    role:         'dialog',
    'aria-modal': 'true',
    'aria-label': 'Book an appointment',
  }, closeBtn, iframe)

  const backdrop = el('div', { class: 'mp-backdrop' })

  // Append to body
  document.body.appendChild(backdrop)
  document.body.appendChild(modal)

  // Focus trap
  const trap = createFocusTrap({
    container: modal,
    onEscape:  close,
  })

  // postMessage listener — v1 protocol
  const unsubscribe = listenForMessages({
    onReady: () => {
      // iframe is loaded — nothing to do in popup mode
    },
    onStepChange: (payload) => {
      emitEvent('step-change', payload)
      dispatchDOMEvent('managepatients:stepChange', payload)
    },
    onBookingComplete: (payload) => {
      emitEvent('booking-complete', payload)
      dispatchDOMEvent('managepatients:bookingComplete', payload)
      // Don't auto-close — the iframe shows a confirmation screen.
      // The iframe sends mp:close-request when the user is done.
    },
    onBookingError: (payload) => {
      emitEvent('booking-error', payload)
      dispatchDOMEvent('managepatients:bookingError', payload)
    },
    onCloseRequest: () => close(),
  })

  function open(): void {
    backdrop.classList.add('mp-visible')
    modal.classList.add('mp-visible')
    trap.activate()
  }

  function close(): void {
    backdrop.classList.remove('mp-visible')
    modal.classList.remove('mp-visible')
    trap.deactivate()
    emitEvent('close', null)
  }

  function destroy(): void {
    unsubscribe()
    backdrop.remove()
    modal.remove()
    cache.delete(key)
  }

  // Wire up UI events
  closeBtn.addEventListener('click', close)
  backdrop.addEventListener('click', close)

  // Listen for programmatic close-all
  window.addEventListener('mp:close-all', close as EventListener)

  const instance: PopupInstance = { open, close, destroy }
  cache.set(key, instance)
  return instance
}

export function mountPopup(trigger: HTMLElement, config: EmbedConfig): () => void {
  const popup = getOrCreatePopup(config, trigger)

  function handleClick(e: Event): void {
    e.preventDefault()
    popup.open()
  }

  trigger.addEventListener('click', handleClick)

  // Return cleanup
  return () => {
    trigger.removeEventListener('click', handleClick)
  }
}
